import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { FiLoader } from 'react-icons/fi';

interface Question {
    id: number;
    text: string;
}

interface Answer {
    id: number;
    testo: string;
}

interface MessageState {
    type: 'success' | 'error';
    text: string;
    visible: boolean;
}

const AssociateAnswer: React.FC = () => {
    const { register, handleSubmit, reset } = useForm({
        defaultValues: {
            question_id: "",
            answere_id: "",
            function: ""
        }
    });

    const [questions, setQuestions] = useState<Question[]>([]);
    const [answers, setAnswers] = useState<Answer[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState<MessageState | null>(null);

    // Gestion des messages temporaires
    useEffect(() => {
        if (message?.visible) {
            const timer = setTimeout(() => {
                setMessage(prev => prev ? { ...prev, visible: false } : null);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [message]);

    const showMessage = (type: 'success' | 'error', text: string) => {
        setMessage({ type, text, visible: true });
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [questionsRes, answersRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL}/questions`),
                    axios.get(`${process.env.REACT_APP_API_URL}/answers`)
                ]);
                setQuestions(questionsRes.data);
                setAnswers(answersRes.data);
            } catch (error) {
                showMessage('error', "Errore durante il caricamento dei dati");
            }
        };
        fetchData();
    }, []);

    const onSubmit = async (data: any) => {
        console.log('Dati del modulo inviati:', data);
        setIsSubmitting(true);

        if (data.question_id === '' || data.answere_id === '') {
            showMessage('error', 'I nomi della domanda e della risposta sono obbligatori.');
            setIsSubmitting(false);
            return;
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/question_answers`, data);
            showMessage('success', 'Associazione creata con successo!');
            reset();
        } catch (error: any) {
            showMessage('error', error.response?.data?.error || "Si è verificato un errore");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-md border border-gray-200/80 transition-all duration-300 hover:shadow-lg">
            <div className="px-4 py-3 border-b bg-gradient-to-r from-[rgb(149,71,254)] via-[rgb(89,96,211)] to-[rgb(51,112,183)]">
                <h2 className="font-bold text-white text-xl">Associare una risposta</h2>
            </div>
            <div className="p-4">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                    {message && (
                        <div
                            className={`p-3 rounded-lg transition-all duration-300 ${
                                message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            } ${message.visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
                            style={{
                                transform: message.visible ? 'translateY(0)' : 'translateY(-10px)',
                                transition: 'all 0.3s ease-out'
                            }}
                        >
                            {message.text}
                        </div>
                    )}
                    
                    {/* Reste du formulaire reste identique */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Domanda</label>
                        <input
                            list="questions-list"
                            {...register("question_id")}
                            placeholder="Cercare una domanda..."
                            autoComplete="off"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                        />
                        <datalist id="questions-list">
                            {questions.slice().sort((a, b) => b.id - a.id).map(q => (
                                <option key={q.id} value={q.text} />
                            ))}
                        </datalist>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Risposta</label>
                        <input
                            list="answers-list"
                            {...register("answere_id")}
                            placeholder="Cercare una risposta..."
                            autoComplete="off"
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring focus:ring-indigo-500 focus:ring-opacity-50"
                        />
                        <datalist id="answers-list">
                            {answers.slice().sort((a, b) => b.id - a.id).map(a => (
                                <option key={a.id} value={a.testo} />
                            ))}
                        </datalist>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Funzione (opzionale)
                        </label>
                        <input
                            type="text"
                            {...register('function')}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-4 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-600 flex items-center justify-center transition-all"
                    >
                        {isSubmitting ? (
                            <>
                                <FiLoader className="animate-spin mr-2" />
                                Associazione in corso...
                            </>
                        ) : (
                            'Associare'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AssociateAnswer;